<template>
  <div ref="CRENEAUX">
    <div class="ps-4">
      <section-header ref="CRENEAUX"
                      :dialog="true"
                      :ressource="ressource"
                      @close-dialog="closeDialog">
      </section-header>
    </div>
    <div v-if="canEdit"
         class="d-flex flex-row-reverse py-4">
      <loader-button class="ms-2" :label="newLabel" @callback="newCreneau"></loader-button>
      <loader-button :disabled="selectedCreneaux.length == 0" class="ms-2" label="Dupliquer"
                     @callback="duplicateCreneau"></loader-button>
    </div>
    <div class="pt-5">
      <v-col class="col-12 pt-md-0 ps-0">
        <v-data-table v-model="selected"
                      dense fixed-header
                      :headers="headers"
                      :items="items"
                      :items-per-page="-1"
                      :must-sort="false"
                      :mobile-breakpoint="100"
                      class="white"
                      no-data-text="Aucune donnée"
                      return-object
                      :disable-pagination="true"
                      :hide-default-footer="true"
                      :show-select="canEdit">
          <template #[`item.date`]="{ item, index }">
            <v-menu :ref="'menu'+index"
                    v-model="item.menuActive"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto">
              <template #activator="{ props }">
                <v-text-field :value="item.date" readonly v-bind="props"></v-text-field>
              </template>
              <v-locale-provider locale="fr">
                <v-date-picker :min="minDate"
                               :max="maxDate"
                               :first-day-of-week="1"
                               :allowed-dates="canEdit ? () => true : () => false"
                               no-title
                               scrollable
                               @update:model-value="(value) => setDate(value, index)"></v-date-picker>
              </v-locale-provider>
            </v-menu>
          </template>
          <template #[`item.heureDebut`]="{ item, index }">
            <creneau-heure-debut :ref="'HEURE_DEBUT'+index"
                                 :value="item.heureDebut"
                                 :read-only="item.date == null || !canEdit"
                                 :hide-label="true"
                                 :required="true"
                                 :context-id="item.id"
                                 @update:value="(value) => setHeureDebut(value, index)">
            </creneau-heure-debut>
          </template>

          <template #[`item.heureFin`]="{ item, index }">
            <creneau-heure-fin :ref="'HEURE_FIN'+index"
                               :value="item.heureFin"
                               :read-only="item.date == null || !canEdit"
                               :hide-label="true"
                               :required="true"
                               :context-id="item.id"
                               @update:value="(value) => setHeureFin(value, index)">
            </creneau-heure-fin>
          </template>

          <template #[`item.activiteId`]="{ item, index }">
            <activite-select :ref="'ACTIVITY_ID'+index"
                             :value=item.activiteId
                             :list-activite-id=listActiviteId
                             :index="index"
                             :toggle-reset=resetActivity
                             :ressource=activiteRessource
                             :contrat=contrat
                             :context-id=100*item.id+index
                             :read-only="item.date == null || !canEdit"
                             :required=true
                             :hide-label="true"
                             @update:value="(value) => setActivityId(value, index)">
            </activite-select>
          </template>

          <template #[`item.lieuId`]="{ item, index }">
            <lieu-summary :ref="'LIEU_ID'+index"
                          :value=item.lieuId
                          parent-type="Contrat"
                          :owner=owner
                          :fiche="fiche"
                          :parent-obj=item
                          :read-only="item.date == null || !canEdit"
                          :required=false
                          :ressource="ressourceAdresse"
                          :hide-label="true"
                          section-name="CreneauxEditor"
                          :context-id="item.id"
                          @update:value="(value) => setLieuId(value, index)">
            </lieu-summary>
          </template>

          <template #[`item.action`]="{ item, index }">
            <v-col class="col-1 px-1 py-3 d-flex flex-column justify-center">
              <div class="d-flex justify-center" @click="deleteCreneau(item, index)">
                <v-icon size="25" color="border">fas fa-trash-can</v-icon>
              </div>
            </v-col>
          </template>
        </v-data-table>

      </v-col>

      <v-row class="justify-end pb-2 pt-4">
        <loader-button v-if="canEdit"
                       class="ms-2"
                       :label="deleteLabel"
                       :disabled="!disableDelete"
                       @callback="deleteItems">
        </loader-button>
        <loader-button v-if="canEdit"
                       class="ms-2"
                       type="submit"
                       :loading="loading"
                       :label="saveLabel"
                       :disabled="!disableSave"
                       @callback="save">
        </loader-button>
      </v-row>
    </div>
  </div>
</template>

<script lang="ts">
  import { storeCreneau } from '@/generated/stores';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import ActiviteSelect from '@/extend/controls/activite/ActiviteSelect';
  import LieuSummary from '@/extend/controls/lieu/LieuSummary';
  import Contrat from '@/extend/classes/Contrat';
  import { ContratStatuts } from '@/generated/classes/Contrat';
  import Creneau from '@/extend/classes/Creneau';
  import { Alerts } from '@/framework/base/helpers/Alerts';
  import {
    AppButtons,
    AppSections,
    FwkButtons,
    RessourceAppButton,
    RessourceAppSection,
    RessourceFwkButton
  } from '@/ressources/globalRessources';
  import { RessourceGenControl, GenControl } from '@/generated/ressources/generatedRessources';
  import CreneauHeureDebut from '@/extend/controls/creneau/CreneauHeureDebut';
  import CreneauHeureFin from '@/extend/controls/creneau/CreneauHeureFin';
  import Owner from '@/extend/classes/Owner';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { defineComponent, type PropType } from "vue";

  export default defineComponent({
    name: 'CreneauxEditor',
    components: {
      LoaderButton,
      SectionHeader,
      ActiviteSelect,
      LieuSummary,
      CreneauHeureDebut,
      CreneauHeureFin,
    },
    props: {
      contrat: {
        type: Object as PropType<Contrat>,
        required: true,
      },
      creneaux: {
        type: Array as PropType<Creneau[]>,
        required: false,
      },
      creneauId: {
        type: Number,
        required: false,
      },
      closeSection: {
        type: Boolean,
        required: false,
      },
      fiche: {
        type: String,
        required: false,
      },
    },
    data() {
      const minDate = null as string | null;
      const maxDate = null as string | null;
      return {
        minDate: minDate,
        maxDate: maxDate,
        listActiviteId: [] as number[],
        cacheCreneaux: [] as Creneau[],
        selectedCreneaux: [] as Creneau[],
        objType: ObjTypes.PLAGE_CRENEAU,
        saving: false,
        loading: false,
        canSave: false,
        canDelete: false,
        resetActivity: true,
      };
    },
    computed: {
      activiteRessource(): any {
        return RessourceGenControl(GenControl.CRENEAUX_ACTIVITE_SELECT);
      },
      ressourceAdresse(): any {
        return RessourceGenControl(GenControl.CRENEAUX_LIEU_SUMMARY);
      },
      saveLabel(): any {
        return RessourceFwkButton(FwkButtons.ENREGISTRER_CRENEAUX).label;
      },
      deleteLabel(): any {
        return RessourceFwkButton(FwkButtons.SUPPRIMER).label;
      },
      ressource(): any {
        return RessourceAppSection(AppSections.CRENEAUX);
      },
      newLabel(): any {
        return RessourceAppButton(AppButtons.AJOUTER_DATE).label;
      },
      headers(): any {
        const headers = [
          { title: "Date", width: '15%', key: 'date', cellClass: '', sortable: false },
          { title: "Début", width: '15%', key: 'heureDebut', cellClass: '', sortable: false },
          { title: "Fin", width: '15%', key: 'heureFin', cellClass: '', sortable: false },
          { title: "Activité", width: '25%', key: 'activiteId', cellClass: '', sortable: false },
          { title: "Lieu", width: '25%', key: 'lieuId', cellClass: '', sortable: false },
        ];
        if (this.canEdit) {
          headers.splice(5, 0, { title: 'Action(s)', width: '5%', key: 'action', sortable: false });
        }
        return headers;
      },
      owner(): Owner {
        const owner = new Owner();
        owner.objType = ObjTypes.CONTRAT;
        owner.objId = this.contrat.id;
        return owner;
      },
      items(): Creneau[] {
        const creneaux = storeCreneau.listCreneau.filter(a => a.contratId == this.contrat.id);
        creneaux.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (dateA - dateB !== 0) {
            return dateA - dateB;
          } else if (a.heureDebut && b.heureDebut) {
            return a.heureDebut.localeCompare(b.heureDebut);
          }
        });
        return creneaux;
      },
      selected: {
        get(): Creneau[] {
          return this.selectedCreneaux;
        },
        set(item: Creneau[]) {
          this.selectedCreneaux = item;
        },
      },
      disableSave(): boolean {
        return this.canSave && this.canEdit;
      },
      disableDelete(): boolean {
        return this.selectedCreneaux.length > 0 && this.canEdit;
      },
      canEdit(): boolean {
        return (this.contrat.statut < ContratStatuts.SUBMITTED || this.contrat.statut == ContratStatuts.MODIFIED);
      },
    },
    created(): void {
      const creneaux = storeCreneau.listCreneau.filter(a => a.contratId == this.contrat.id);
      if (creneaux.length == 0) {
        this.newCreneau();
        this.listActiviteId = [];
      }
      this.items.forEach((item) => {
        this.cacheCreneaux.push(item.cloneCreneau())
        this.listActiviteId.push(item.activiteId);
      })
      let date = new Date();
      if (creneaux.length > 1) {
        date = new Date(creneaux[this.items.length - 1].date);
        this.minDate = new Date(date.getFullYear(), date.getMonth(), 1);
        this.maxDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      }
    },
    beforeUnmount(): void {
      if (!this.closeSection) {
        this.beforeClose();
      }
    },
    methods: {
      beforeClose(): void {
        if (!this.saving) {
          this.saving = true;
          if (this.disableSave) {
            this.dialogSaveOrCancel();
          } else {
            // Virer les créneaux non renseigné et non sélectionnés
            this.items.filter(a => a.id < 0).forEach((creneau) => {
              storeCreneau.mCreneauDeleted(creneau.id);
            })
            this.$emit('cancelled');
            this.saving = false;
          }
        }
      },
      resetMaxDate(): void {
        if (this.items.length == 1) {
          this.minDate = null;
          this.maxDate = null;
        }
      },
      deleteCreneau(creneau: Creneau, index: number): void {
        storeCreneau.mCreneauDeleted(creneau.id);
        this.resetActivity = !this.resetActivity;
        this.resetMaxDate();
        this.updateCanApply();
      },
      deleteItems(): void {
        this.selectedCreneaux.forEach((creneau) => {
          storeCreneau.mCreneauDeleted(creneau.id);
        })
        this.resetMaxDate();
        this.updateCanApply();
        this.selectedCreneaux = [];
      },
      newCreneau(): void {
        let date = new Date();
        if (this.items.length > 0) {
          date = new Date(this.items[this.items.length - 1].date);
          if (!this.maxDate || (this.maxDate && date.getDate() != this.maxDate.getDate())) {
            date.setDate(this.items[this.items.length - 1].date.getDate() + 1);
          }
        }
        if (this.items.length == 1) {
          const lastDate = new Date(this.items[0].date);
          this.minDate = new Date(lastDate.getFullYear(), lastDate.getMonth(), 1);
          this.maxDate = new Date(lastDate.getFullYear(), lastDate.getMonth() + 1, 0);
        }
        const creneau = storeCreneau.createNewCreneau();
        creneau.contratId = this.contrat.id;
        creneau.date = date;
      },
      duplicateCreneau(): void {
        if (this.items.length == 1) {
          const lastDate = new Date(this.items[0].date);
          this.minDate = new Date(lastDate.getFullYear(), lastDate.getMonth(), 1);
          this.maxDate = new Date(lastDate.getFullYear(), lastDate.getMonth() + 1, 0);
        }
        this.selectedCreneaux.forEach((item) => {
          const originalDate = new Date(item.date);
          const year = originalDate.getFullYear();
          const month = originalDate.getMonth();
          const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
          const creneau = storeCreneau.createNewCreneau();
          creneau.contratId = item.contratId;
          const newDate = new Date(originalDate);
          if (originalDate.getDate() < lastDayOfMonth) {
            newDate.setDate(newDate.getDate() + 1);
          } else {
            newDate.setDate(newDate.getDate());
          }
          creneau.date = newDate;
          creneau.heureDebut = item.heureDebut;
          creneau.heureFin = item.heureFin;
          creneau.activiteId = item.activiteId;
          creneau.lieuId = item.lieuId;
        });
        this.updateCanApply();
      },
      setDate(value: string, index: number) {
        this.items[index].menuActive = false;
        const localDate = new Date(value);
        const utcDate = new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000);
        this.items[index].date = utcDate;
        this.updateCanApply();
      },
      setHeureDebut(value: string | number, index: number): void {
        this.items[index].heureDebut = value.toString();
        this.updateCanApply();
      },
      setHeureFin(value: string | number, index: number): void {
        this.items[index].heureFin = value.toString();
        this.updateCanApply();
      },
      setActivityId(value: number, index: number): void {
        this.items[index].activiteId = value;
        if (value > 0) {
          this.updateCanApply();
          this.resetActivity = !this.resetActivity;
        }
      },
      setLieuId(value: number, index: number): void {
        this.items[index].lieuId = value;
        if (value > 0) {
          this.updateCanApply();
        }
      },
      compare(c1: Creneau, c2: Creneau): boolean {
        const changed = c1.contratId != c2.contratId ||
          c1.date.getDate() != c2.date.getDate() ||
          c1.heureDebut != c2.heureDebut ||
          c1.heureFin != c2.heureFin ||
          c1.activiteId != c2.activiteId ||
          c1.lieuId != c2.lieuId ||
          c1.plages.length != c2.plages.length ||
          this.items.length != this.cacheCreneaux.length;
        return !changed;
      },
      updateCanApply() {
        let updated = false;
        this.listActiviteId = [];
        this.items.forEach((item) => {
          this.listActiviteId.push(item.activiteId);
          if (!updated) {
            if (item.id == 0) {
              updated = true;
            } else {
              const creneau = this.cacheCreneaux.find(a => a.id == item.id);
              if (creneau) {
                updated = !this.compare(creneau, item);
              }
            }
          }
        });
        this.canSave = updated;
      },
      dialogSaveOrCancel(): Promise<boolean> {
        return new Promise((resolve) => {
          if (this.disableSave) {
            Alerts.saveOrCancel()
              .then((result: boolean) => {
                if (result) {
                  this.save();
                } else {
                  this.items.forEach((creneau) => {
                    if (creneau.id < 0) {
                      storeCreneau.mCreneauDeleted(creneau.id);
                    } else {
                      const ref = this.cacheCreneaux.find(a => a.id == creneau.id);
                      if (ref) {
                        creneau.heureDebut = ref.heureDebut;
                        creneau.heureFin = ref.heureFin;
                        creneau.date = ref.date;
                        creneau.activiteId = ref.activiteId;
                        creneau.lieuId = ref.lieuId;
                      }
                    }
                  })
                  if (this.cacheCreneaux.length > this.items.length) {
                    this.cacheCreneaux.forEach((creneau) => {
                      if (!this.items.find(a => a.id == creneau.id)) {
                        storeCreneau.getCreneau(creneau.id);
                      }
                    })

                  }
                  this.$emit('cancelled');
                  this.saving = false;
                }
                resolve(result);
              });
          }
        });
      },
      checkFields(): boolean {
        let error = false;
        this.items.forEach((item, index) => {
          const date = new Date(item.date);
          if (!error && date.getFullYear() != 1901) {
            if (this.$refs['HEURE_DEBUT' + index]) {
              // @ts-expect-error : generated method
              if (this.$refs['HEURE_DEBUT' + index].touch()) {
                error = true;
              }
            }
            if (this.$refs['HEURE_FIN' + index]) {
              // @ts-expect-error : generated method
              if (this.$refs['HEURE_FIN' + index].touch()) {
                error = true;
              }
            }
            if (this.$refs['ACTIVITY_ID' + index]) {
              // @ts-expect-error : generated method
              if (this.$refs['ACTIVITY_ID' + index] && this.$refs['ACTIVITY_ID' + index].touch()) {
                error = true;
              }
            }
            if (this.$refs['LIEU_ID' + index]) {
              // @ts-expect-error : generated method
              if (this.$refs['LIEU_ID' + index].touch()) {
                error = true;
              }
            }
          }
        })
        return error;
      },
      save(): void {
        if (!this.checkFields()) {
          this.loading = true;
          Creneau.updateCreneaux(this.items).then(() => {
            this.loading = false;
            this.closeDialog();
          }).catch(() => {
            this.loading = false;
          });
        }
        this.canSave = false;
      },
      closeDialog() {
        this.$emit('closeDialog');
      },
    },
  });
</script>